import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, Star, Settings, Eye, Trash2, CheckCircle, XCircle, Edit, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  const [bookings, setBookings] = useState([]);

  const [reviews, setReviews] = useState([]);

  const [services, setServices] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { getBookings, getAllReviews, getServices } = await import('@/utils/api');
      const [bookingsData, reviewsData, servicesData] = await Promise.all([
        getBookings(),
        getAllReviews(),
        getServices()
      ]);
      setBookings(bookingsData);
      setReviews(reviewsData);
      setServices(servicesData);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Déconnexion réussie');
    navigate('/admin/login');
  };

  const updateBookingStatus = async (id, status) => {
    try {
      const { updateBooking } = await import('@/utils/api');
      await updateBooking(id, status);
      setBookings(bookings.map(b => b.id === id ? { ...b, status } : b));
      toast.success(`Réservation ${status === 'confirmed' ? 'confirmée' : 'annulée'}`);
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteBooking = async (id) => {
    try {
      const { deleteBooking: deleteBookingApi } = await import('@/utils/api');
      await deleteBookingApi(id);
      setBookings(bookings.filter(b => b.id !== id));
      toast.success('Réservation supprimée');
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const updateReviewStatus = async (id, status) => {
    try {
      const { updateReview } = await import('@/utils/api');
      await updateReview(id, status);
      setReviews(reviews.map(r => r.id === id ? { ...r, status } : r));
      toast.success(`Avis ${status === 'approved' ? 'approuvé' : 'rejeté'}`);
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteReview = async (id) => {
    try {
      const { deleteReview: deleteReviewApi } = await import('@/utils/api');
      await deleteReviewApi(id);
      setReviews(reviews.filter(r => r.id !== id));
      toast.success('Avis supprimé');
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Confirmée', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Annulée', className: 'bg-red-100 text-red-800' },
      approved: { label: 'Approuvé', className: 'bg-green-100 text-green-800' },
      rejected: { label: 'Rejeté', className: 'bg-red-100 text-red-800' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const stats = {
    totalBookings: bookings.length,
    pendingBookings: bookings.filter(b => b.status === 'pending').length,
    totalReviews: reviews.length,
    pendingReviews: reviews.filter(r => r.status === 'pending').length,
  };

  return (
    <div className="min-h-screen pt-20 pb-20 bg-[#f8f7f5]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="hero-title text-4xl md:text-5xl mb-2">Panneau d'Administration</h1>
            <p className="text-gray-600">Gestion de SUKI Montmagny</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="flex items-center space-x-2"
            data-testid="admin-logout-button"
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Réservations</p>
                  <p className="text-3xl font-bold text-[#1a1a1a]">{stats.totalBookings}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#c9a961]/10 flex items-center justify-center">
                  <Calendar className="text-[#c9a961]" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">En attente</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pendingBookings}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Calendar className="text-yellow-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Avis total</p>
                  <p className="text-3xl font-bold text-[#1a1a1a]">{stats.totalReviews}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#c9a961]/10 flex items-center justify-center">
                  <Star className="text-[#c9a961]" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Avis à modérer</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pendingReviews}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Star className="text-yellow-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">
              <Calendar className="mr-2" size={18} />
              Réservations
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews">
              <Star className="mr-2" size={18} />
              Avis
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="admin-tab-services">
              <Settings className="mr-2" size={18} />
              Services
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="section-title text-2xl">Gestion des Réservations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {bookings.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Aucune réservation</p>
                  ) : (
                    bookings.map(booking => (
                      <Card key={booking.id} className="border" data-testid={`booking-${booking.id}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h3 className="font-semibold text-lg">{booking.name}</h3>
                                {getStatusBadge(booking.status)}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                                <p>
                                  <strong>Service:</strong> {booking.service}
                                </p>
                                <p>
                                  <strong>Véhicule:</strong> {booking.vehicleType}
                                </p>
                                <p>
                                  <strong>Date:</strong> {booking.date} à {booking.time}
                                </p>
                                <p>
                                  <strong>Email:</strong> {booking.email}
                                </p>
                                <p>
                                  <strong>Tél:</strong> {booking.phone}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {booking.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                  className="bg-green-600 hover:bg-green-700"
                                  data-testid={`confirm-booking-${booking.id}`}
                                >
                                  <CheckCircle size={16} className="mr-1" />
                                  Confirmer
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                  className="border-red-600 text-red-600 hover:bg-red-50"
                                  data-testid={`cancel-booking-${booking.id}`}
                                >
                                  <XCircle size={16} className="mr-1" />
                                  Annuler
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteBooking(booking.id)}
                              className="border-gray-300 text-gray-600 hover:bg-gray-50"
                              data-testid={`delete-booking-${booking.id}`}
                            >
                              <Trash2 size={16} className="mr-1" />
                              Supprimer
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="section-title text-2xl">Modération des Avis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Aucun avis</p>
                  ) : (
                    reviews.map(review => (
                      <Card key={review.id} className="border" data-testid={`review-${review.id}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h3 className="font-semibold text-lg">{review.name}</h3>
                                {getStatusBadge(review.status)}
                              </div>
                              <div className="flex items-center space-x-2 mb-2">
                                {[...Array(review.rating)].map((_, i) => (
                                  <Star key={i} size={16} className="text-[#c9a961] fill-current" />
                                ))}
                                <span className="text-sm text-gray-500">({review.rating}/5)</span>
                              </div>
                              <p className="text-gray-700 mb-2">{review.comment}</p>
                              <p className="text-xs text-gray-500">Date: {review.date}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {review.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => updateReviewStatus(review.id, 'approved')}
                                  className="bg-green-600 hover:bg-green-700"
                                  data-testid={`approve-review-${review.id}`}
                                >
                                  <CheckCircle size={16} className="mr-1" />
                                  Approuver
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateReviewStatus(review.id, 'rejected')}
                                  className="border-red-600 text-red-600 hover:bg-red-50"
                                  data-testid={`reject-review-${review.id}`}
                                >
                                  <XCircle size={16} className="mr-1" />
                                  Rejeter
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteReview(review.id)}
                              className="border-gray-300 text-gray-600 hover:bg-gray-50"
                              data-testid={`delete-review-${review.id}`}
                            >
                              <Trash2 size={16} className="mr-1" />
                              Supprimer
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="section-title text-2xl">Gestion des Services</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {services.map(service => (
                    <Card key={service.id} className="border" data-testid={`service-${service.id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-semibold text-xl section-title">{service.name}</h3>
                              <Badge className={service.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                {service.active ? 'Actif' : 'Inactif'}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                              <p>
                                <strong>Prix:</strong> {service.price}
                              </p>
                              <p>
                                <strong>Durée:</strong> {service.duration}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" data-testid={`edit-service-${service.id}`}>
                              <Edit size={16} className="mr-1" />
                              Modifier
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
